import styles from "./Payment.module.css";
import type { ReactNode } from "react";

type IncludedItem = {
  number: string;
  title: string;
  description: string;
  icon: ReactNode;
};

const MagnifierIcon = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M11 19a8 8 0 1 1 0-16 8 8 0 0 1 0 16Z" stroke="currentColor" strokeWidth="2" />
    <path d="M21 21l-4.3-4.3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const ClipboardIcon = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M9 3h6a2 2 0 0 1 2 2v2H7V5a2 2 0 0 1 2-2Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path d="M7 7h10v14H7V7Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    <path
      d="M9.5 14l1.5 1.5L14.5 12"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ShieldIcon = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M12 2 20 6v7c0 5-3.5 8.5-8 9-4.5-.5-8-4-8-9V6l8-4Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
  </svg>
);

const SparklesIcon = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M12 2l1.2 4.1L17 7.3l-3.8 1.2L12 12l-1.2-3.5L7 7.3l3.8-1.2L12 2Z"
      fill="currentColor"
    />
    <path d="M19 13l.8 2.6L22 16.4l-2.2.7L19 20l-.8-2.9-2.2-.7 2.2-.8L19 13Z" fill="currentColor" />
    <path d="M4.8 13l.7 2.1 2 .7-2 .6-.7 2.3-.7-2.3-2-.6 2-.7.7-2.1Z" fill="currentColor" />
  </svg>
);

// const RefreshIcon = () => (
//   <svg
//     width="28"
//     height="28"
//     viewBox="0 0 24 24"
//     fill="none"
//     xmlns="http://www.w3.org/2000/svg"
//     aria-hidden="true"
//     focusable="false"
//   >
//     <path
//       d="M20 6v6h-6"
//       stroke="currentColor"
//       strokeWidth="2"
//       strokeLinecap="round"
//       strokeLinejoin="round"
//     />
//     <path
//       d="M4 18v-6h6"
//       stroke="currentColor"
//       strokeWidth="2"
//       strokeLinecap="round"
//       strokeLinejoin="round"
//     />
//     <path d="M20 12a8 8 0 0 0-14.8-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
//     <path d="M4 12a8 8 0 0 0 14.8 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
//   </svg>
// );

// const InfoIcon = () => (
//   <svg
//     width="18"
//     height="18"
//     viewBox="0 0 24 24"
//     fill="none"
//     xmlns="http://www.w3.org/2000/svg"
//     aria-hidden="true"
//     focusable="false"
//   >
//     <path d="M12 22a10 10 0 1 1 0-20 10 10 0 0 1 0 20Z" stroke="currentColor" strokeWidth="2" />
//     <path d="M12 10.7v6.3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
//     <path d="M12 7.2h.01" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
//   </svg>
// );

export const Payment = () => {
  const items: IncludedItem[] = [
    {
      number: "01",
      title: "Deep Scan",
      description: "Analyzing neighbors and\nmarket trends.",
      icon: <MagnifierIcon />,
    },
    {
      number: "02",
      title: "Evidence",
      description: "Crafting the perfect legal\ndossier.",
      icon: <ClipboardIcon />,
    },
    {
      number: "03",
      title: "Hearing",
      description: "Elite representation at\nthe board.",
      icon: <ShieldIcon />,
    },
    {
      number: "04",
      title: "Results",
      description: "Tax reduction locked in\nforever.",
      icon: <SparklesIcon />,
    },
  ];

  return (
    <section className={styles.section}>
      <div className={styles.inner}>
        <h2 className={styles.title}>
          What&apos;s <span className={styles.titleAccent}>included</span> in the fee?
        </h2>

        <div className={styles.flow}>
          <div className={styles.connector} aria-hidden="true" />
          {items.map((item) => (
            <div key={item.number} className={styles.item}>
              <div className={styles.badge}>{item.number}</div>

              <div className={styles.card}>
                <div className={styles.iconWrap} aria-hidden="true">
                  {item.icon}
                </div>
                <div className={styles.cardTitle}>{item.title}</div>
                <div className={styles.cardDesc}>{item.description}</div>
              </div>
            </div>
          ))}
        </div>

        <div className={styles.autoPayWrap}>
          {/* <div className={styles.autoPayCard}>
            <div className={styles.autoPayHeader}>
              <div className={styles.autoPayIcon} aria-hidden="true">
                <RefreshIcon />
              </div>
              <div className={styles.autoPayText}>
                <div className={styles.autoPayTitle}>Enable Auto-Pay (Annual Subscription)</div>
                <div className={styles.autoPaySubtitle}>
                  Automatically renew your protest filing fee each year
                </div>
              </div>
            </div>

            <div className={styles.autoPayDivider} aria-hidden="true" />

            <div className={styles.autoPayNote}>
              <span className={styles.autoPayNoteIcon} aria-hidden="true">
                <InfoIcon />
              </span>
              <span className={styles.autoPayNoteText}>
                Your card will be charged $99 annually. You can cancel anytime.
              </span>
            </div>
          </div> */}
        </div>
      </div>
    </section>
  );
};
